import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Phone, 
  ArrowRight, 
  ArrowDown,
  Plus, 
  Minus, 
  Quote, 
  CheckCircle2, 
  Info, 
  AlertCircle, 
  ShieldCheck, 
  EyeOff, 
  MessageSquareOff, 
  Coffee, 
  Sliders, 
  Sparkles, 
  MapPin, 
  Laptop, 
  HelpCircle,
  Coins,
  FileText,
  ArrowUp,
  ChevronDown
} from 'lucide-react';

// FAQ Accordion Component with Framer Motion
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="bg-white border border-[#E8DDCE] rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-[#FAF7F2] transition-colors group cursor-pointer"
        aria-expanded={isOpen}
      >
        <span className="font-bold text-[#123F13] md:text-lg pr-8">{question}</span>
        <div className={`shrink-0 text-[#123F13] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed border-t border-[#FAF7F2] pt-4">
              <div className="space-y-4">
                {answer}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SocialAngst = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const canonicalUrl = "https://corehypnose.dk/social-angst";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Er social angst det samme som generthed?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Det afgørende er ikke, om du er stille eller udadvendt. Det afgørende er, hvor meget frygten styrer din opmærksomhed, din tid og dine valg."
        }
      },
      {
        "@type": "Question",
        "name": "Hvorfor går jeg blank, selv om jeg egentlig ved, hvad jeg vil sige?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Selvfokus og mental kontrol kan bruge så meget kapacitet, at der bliver mindre kontakt med selve samtalen."
        }
      },
      {
        "@type": "Question",
        "name": "Hvorfor gennemgår jeg samtaler igen og igen bagefter?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Den mentale gennemgang kan føles som problemløsning, men den kan ende i mere tvivl og større frygt for næste situation."
        }
      },
      {
        "@type": "Question",
        "name": "Hvorfor bliver jeg så opmærksom på min stemme, mit ansigt og min krop?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Det, der skal give kontrol, kan skabe en cirkel: mere tjekken → mere registreret uro → større behov for at tjekke."
        }
      },
      {
        "@type": "Question",
        "name": "Skal jeg presses ud i sociale situationer som en del af behandlingen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Formålet er ikke at overvælde dig. Jeg arbejder med tydelige, tilpassede skridt og med at forstå, hvad der holder mønstret i gang."
        }
      },
      {
        "@type": "Question",
        "name": "Kan jeg få det bedre, selv om jeg stadig bliver nervøs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "En vigtig forandring kan være, at nervøsitet ikke længere styrer så meget af din opmærksomhed, din tid og dine valg."
        }
      },
      {
        "@type": "Question",
        "name": "Hvad sker der, hvis jeg rødmer, ryster eller får panik?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Jeg arbejder både med den kropslige uro, opmærksomheden på den og de strategier, du bruger for at kontrollere eller skjule den."
        }
      },
      {
        "@type": "Question",
        "name": "Kan jeg få hjælp, hvis jeg også har ADHD eller autisme?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Det er vigtigt ikke at forklare alt som social angst. Behandlingen skal tage udgangspunkt i dit konkrete mønster og dine behov."
        }
      },
      {
        "@type": "Question",
        "name": "Skal jeg bruge hypnose?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hypnose er en mulighed – ikke et krav. Den kan bruges som støtte og kan også helt vælges fra."
        }
      },
      {
        "@type": "Question",
        "name": "Kan jeg starte med én session og først derefter beslutte, hvad jeg vil?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Du kan starte med én første behandlingssession til 375 kr. Der er ingen binding til et længere forløb."
        }
      }
    ]
  };

  const faqData = [
    {
      question: "Er social angst det samme som generthed?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ikke nødvendigvis</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Generthed kan være et personlighedstræk</div>
              <p className="text-xs text-[#3B352F]/80">Det behøver ikke være et problem eller begrænse dit liv.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Social angst optager mere</div>
              <p className="text-xs text-[#3B352F]/80">Frygt for vurdering, selvfokus og grubleri kan tage meget tid og energi.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Se på friheden</div>
              <p className="text-xs text-[#3B352F]/80">Det afgørende er, hvor meget frygten styrer din opmærksomhed og dine valg.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Det afgørende er ikke, om du er stille eller udadvendt. Det afgørende er, <strong>hvor meget frygten styrer din opmærksomhed, din tid og dine valg.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/angst" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om angst &rarr;</Link>
            <Link to="/behandlinger" className="text-xs font-bold text-[#8C2F12] hover:underline">Se behandlinger &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvorfor går jeg blank, selv om jeg egentlig ved, hvad jeg vil sige?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Opmærksomheden bliver optaget</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du holder øje med dig selv</div>
              <p className="text-xs text-[#3B352F]/80">Meget opmærksomhed går til stemme, ansigt, krop og hvordan du virker.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du prøver at kontrollere</div>
              <p className="text-xs text-[#3B352F]/80">Du forsøger måske at styre ord, stemme eller indtryk imens du taler.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Der bliver mindre plads til samtalen</div>
              <p className="text-xs text-[#3B352F]/80">Når meget foregår indeni, kan ordene føles sværere at få fat i.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Selvfokus og mental kontrol kan bruge så meget kapacitet, at der bliver <strong>mindre kontakt med selve samtalen.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
            <Link to="/tankemylder" className="text-xs font-bold text-[#8C2F12] hover:underline">Tankemylder &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvorfor gennemgår jeg samtaler igen og igen bagefter?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Fordi hovedet søger sikkerhed</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du gennemgår samtalen</div>
              <p className="text-xs text-[#3B352F]/80">Du spiller øjeblikke igennem for at forstå, hvad der skete.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du leder efter fejl</div>
              <p className="text-xs text-[#3B352F]/80">Du prøver at finde tegn på, at noget gik galt.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Tvivlen vokser</div>
              <p className="text-xs text-[#3B352F]/80">Jo længere du undersøger, desto flere detaljer kan der være at tvivle på.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Den mentale gennemgang kan føles som problemløsning, men den kan ende i <strong>mere tvivl og større frygt for næste situation.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/tankemylder" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om tankemylder &rarr;</Link>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvorfor bliver jeg så opmærksom på min stemme, mit ansigt og min krop?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Du prøver at opdage uro før andre</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du lytter efter tegn</div>
              <p className="text-xs text-[#3B352F]/80">Du holder øje med stemmen, ansigtet, hænderne, varmen i kroppen og tegn på, at andre opdager din uro.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du mærker mere</div>
              <p className="text-xs text-[#3B352F]/80">Når opmærksomheden er rettet mod kroppen, mærker du uroen endnu tydeligere.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Behovet for kontrol vokser</div>
              <p className="text-xs text-[#3B352F]/80">Det, der skal give kontrol, kan skabe en cirkel: mere tjekken &rarr; mere registreret uro &rarr; større behov for at tjekke.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Det, der skal give kontrol, kan skabe en cirkel: <strong>mere tjekken &rarr; mere registreret uro &rarr; større behov for at tjekke.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
            <Link to="/bestil-tid" className="text-xs font-bold text-[#8C2F12] hover:underline">Book første session &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Skal jeg presses ud i sociale situationer som en del af behandlingen?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Formålet er ikke at overvælde dig</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Ingen overvældelse</div>
              <p className="text-xs text-[#3B352F]/80">Formålet er ikke at overvælde dig eller skabe kontroltab.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Tydeligt og tilpasset</div>
              <p className="text-xs text-[#3B352F]/80">Jeg arbejder med tydelige, tilpassede skridt og med at forstå, hvad der holder mønstret i gang.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Virkelige situationer undersøges</div>
              <p className="text-xs text-[#3B352F]/80">Vi undersøger virkelige situationer og mønstre, så du kan opdage nye måder at handle på.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Formålet er ikke at overvælde dig. Jeg arbejder med <strong>tydelige, tilpassede skridt</strong> og med at forstå, hvad der holder mønstret i gang.
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
            <Link to="/kontakt" className="text-xs font-bold text-[#8C2F12] hover:underline">Kontakt mig &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg få det bedre, selv om jeg stadig bliver nervøs?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ja</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Nervøsitet er naturlig</div>
              <p className="text-xs text-[#3B352F]/80">Nervøsitet og usikkerhed er en del af at være menneske.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Det behøver ikke styre</div>
              <p className="text-xs text-[#3B352F]/80">Det afgørende er, om nervøsiteten styrer din opmærksomhed, din tid og dine valg.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du genvinder friheden</div>
              <p className="text-xs text-[#3B352F]/80">Når mønstret fylder mindre, får du mere frihed til at gøre det, du gerne vil.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            En vigtig forandring kan være, at nervøsitet ikke længere styrer så meget af <strong>din opmærksomhed, din tid og dine valg.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
            <Link to="/bestil-tid" className="text-xs font-bold text-[#8C2F12] hover:underline">Book første session &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvad sker der, hvis jeg rødmer, ryster eller får panik?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Så arbejder jeg med det sammen med dig</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Kroppen må reagere</div>
              <p className="text-xs text-[#3B352F]/80">Rødmen, rysten og panikfølelse kan føles voldsomt uden at være farligt i sig selv.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi ser på opmærksomheden</div>
              <p className="text-xs text-[#3B352F]/80">Hvor meget holder du øje med symptomerne, og hvad sker der, når du gør det?</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi ser på skjul og kontrol</div>
              <p className="text-xs text-[#3B352F]/80">Vi undersøger de strategier, du bruger for at skjule eller kontrollere uroen.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Jeg arbejder både med <strong>den kropslige uro, opmærksomheden på den og de strategier, du bruger for at kontrollere eller skjule den.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/panikangst" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om panikangst &rarr;</Link>
            <Link to="/angst" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om angst &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg få hjælp, hvis jeg også har ADHD eller autisme?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ja</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Ikke alt er social angst</div>
              <p className="text-xs text-[#3B352F]/80">Opmærksomhed, sansebelastning og social træthed kan også spille en rolle.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Tydelige rammer kan hjælpe</div>
              <p className="text-xs text-[#3B352F]/80">Kommunikation og struktur kan tilpasses dit behov.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Behandlingen tilpasses dit mønster</div>
              <p className="text-xs text-[#3B352F]/80">Jeg tager udgangspunkt i det, der faktisk sker for dig.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Det er vigtigt ikke at forklare alt som social angst. <strong>Behandlingen skal tage udgangspunkt i dit konkrete mønster og dine behov.</strong>
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/adhd" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om ADHD &rarr;</Link>
            <Link to="/autismevenlig-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Autismevenlig session &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Skal jeg bruge hypnose?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Hypnose er en mulighed – ikke et krav</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Det er helt valgfrit</div>
              <p className="text-xs text-[#3B352F]/80">Hypnose bruges kun, hvis det giver mening for dig og passer til dit mønster.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Rolig guidning</div>
              <p className="text-xs text-[#3B352F]/80">Jeg bruger hypnose som rolig guidning og fokuseret opmærksomhed.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Kan fravælges</div>
              <p className="text-xs text-[#3B352F]/80">Det kan også helt vælges fra, og vi kan udelukkende arbejde metakognitivt.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Hypnose er en mulighed – ikke et krav. Den kan bruges som støtte og kan også helt vælges fra.
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/hypnose" className="text-xs font-bold text-[#8C2F12] hover:underline">Læs om hypnose &rarr;</Link>
            <Link to="/metakognitiv-terapi" className="text-xs font-bold text-[#8C2F12] hover:underline">Metakognitiv terapi &rarr;</Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg starte med én session og først derefter beslutte, hvad jeg vil?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ja</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Start med én session</div>
              <p className="text-xs text-[#3B352F]/80">Du kan starte med en første behandlingssession til 375 kr.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Ingen binding</div>
              <p className="text-xs text-[#3B352F]/80">Der er ingen binding til et længere forløb eller klippekort.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi vurderer sammen</div>
              <p className="text-xs text-[#3B352F]/80">Vi vurderer sammen, om én session er nok, eller om et forløb giver mening.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Du kan starte med én første behandlingssession til 375 kr. Der er ingen binding til et længere forløb.
          </p>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#FAF7F2]">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider self-center">Læs videre:</span>
            <Link to="/bestil-tid" className="text-xs font-bold text-[#8C2F12] hover:underline">Book første session &rarr;</Link>
            <Link to="/priser" className="text-xs font-bold text-[#8C2F12] hover:underline">Se priser &rarr;</Link>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Social angst – behandling i Valby eller online | Core Hypnose</title>
        <meta name="description" content="Få hjælp til social angst i Valby eller online. Forstå mønstret før, under og efter sociale situationer, og få et konkret første skridt." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Social angst – behandling i Valby eller online | Core Hypnose" />
        <meta property="og:description" content="Få hjælp til social angst i Valby eller online. Forstå mønstret før, under og efter sociale situationer, og få et konkret første skridt." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:py-24 bg-gradient-to-br from-[#FCFAF7] to-[#FAF7F2] border-b border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Column */}
            <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Social angst
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#123F13] leading-tight font-semibold">
                Social angst – når du bliver fanget i, hvordan du virker
              </h1>
              <p className="text-lg md:text-xl text-[#3B352F]/70 leading-relaxed max-w-2xl">
                Du vil måske gerne være til stede sammen med andre. Men før, under og efter sociale situationer kan bekymring, selvmonitorering og grubleri komme til at fylde så meget, at du bliver fanget i dig selv.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link 
                  to="/bestil-tid" 
                  className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-6 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
                >
                  Book første behandlingssession
                </Link>
                <a 
                  href="#moenster" 
                  className="inline-flex items-center justify-center border-2 border-[#123F13] text-[#123F13] px-6 py-4 rounded-lg font-bold text-base hover:bg-[#123F13]/5 transition-all"
                >
                  Se hvad der holder angsten i gang &darr;
                </a>
              </div>
            </div>

            {/* Right Column: Mini Flow Card */}
            <div className="lg:col-span-5 flex items-center">
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm w-full relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-20 h-24 rounded-full bg-[#123F13]/5 pointer-events-none" />
                <div className="relative z-10 space-y-6">
                  <p className="font-bold text-[#123F13] text-lg leading-snug">
                    Det mentale arbejde kan begynde før situationen – og fortsætte længe efter.
                  </p>
                  
                  <div className="space-y-5 pt-2">
                    {/* Step 1 */}
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 bg-[#8C2F12]/10 text-[#8C2F12] px-2.5 py-1 rounded-md font-bold text-xs tracking-wider">
                        FØR
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] text-sm">Hvad nu hvis?</h4>
                        <p className="text-[#3B352F]/70 text-xs mt-0.5">Bekymring og mental forberedelse</p>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-start pl-5 text-[#123F13]/30">
                      <i className="fa-solid fa-arrow-down text-xs" />
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 bg-[#123F13]/10 text-[#123F13] px-2.5 py-1 rounded-md font-bold text-xs tracking-wider">
                        UNDER
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] text-sm">Hvordan virker jeg?</h4>
                        <p className="text-[#3B352F]/70 text-xs mt-0.5">Selvfokus, overvågning og kontrol</p>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-start pl-5 text-[#123F13]/30">
                      <i className="fa-solid fa-arrow-down text-xs" />
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 bg-[#8C2F12]/10 text-[#8C2F12] px-2.5 py-1 rounded-md font-bold text-xs tracking-wider">
                        EFTER
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] text-sm">Hvad gjorde jeg forkert?</h4>
                        <p className="text-[#3B352F]/70 text-xs mt-0.5">Gennemgang, fejlfinding og grubleri</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Er det her dig?" Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-left max-w-3xl mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Er det her dig?</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Måske bruger du mere energi på at overvåge dig selv end på selve situationen
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Social angst kan se forskellig ud. Nogle undgår helt. Andre møder op, men bruger næsten al deres opmærksomhed på at kontrollere, hvordan de virker.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-[#FAF7F2] p-8 rounded-2xl border border-[#E8DDCE] relative overflow-hidden flex flex-col items-start">
              <span className="inline-block bg-[#8C2F12]/10 text-[#8C2F12] px-3 py-1 rounded-full font-bold text-xs mb-4">
                Før
              </span>
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Du øver samtalen, før den er begyndt</h3>
              <p className="text-[#3B352F]/70 leading-relaxed text-sm">
                Du forbereder svar, forestiller dig, hvad der kan gå galt, eller overvejer, hvordan du kan undgå situationen.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-[#FAF7F2] p-8 rounded-2xl border border-[#E8DDCE] relative overflow-hidden flex flex-col items-start">
              <span className="inline-block bg-[#123F13]/10 text-[#123F13] px-3 py-1 rounded-full font-bold text-xs mb-4">
                Under
              </span>
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Du hører mere efter din egen stemme end efter den anden</h3>
              <p className="text-[#3B352F]/70 leading-relaxed text-sm">
                Du holder øje med stemmen, ansigtet, hænderne, varmen i kroppen og tegn på, at andre opdager din uro.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-[#FAF7F2] p-8 rounded-2xl border border-[#E8DDCE] relative overflow-hidden flex flex-col items-start">
              <span className="inline-block bg-[#8C2F12]/10 text-[#8C2F12] px-3 py-1 rounded-full font-bold text-xs mb-4">
                Efter
              </span>
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Du gennemgår samtalen, selv om den er slut</h3>
              <p className="text-[#3B352F]/70 leading-relaxed text-sm">
                Du leder efter fejl, husker bestemte øjeblikke og prøver at regne ud, hvad andre mon tænkte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* "Før · under · efter" Section */}
      <section id="moenster" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Før · under · efter</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            Social angst før, under og efter
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-2xl mx-auto mb-16">
            For mange er det ikke kun selve den sociale situation, der fylder. Det er også alt det mentale arbejde omkring den.
          </p>

          <div className="flex flex-col lg:flex-row items-stretch justify-between gap-6 lg:gap-8 max-w-5xl mx-auto">
            {/* Panel 1: FØR */}
            <div className="flex-1 bg-white border border-[#E8DDCE] rounded-2xl p-8 relative min-h-[220px] flex flex-col justify-between shadow-sm">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#8C2F12] block mb-2">FØR</span>
                <h3 className="text-2xl font-serif text-[#123F13] mb-6 font-semibold">Hvad nu hvis?</h3>
                <ul className="space-y-3 text-left max-w-xs mx-auto text-[#3B352F]/80">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Bekymring
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Mental forberedelse
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Katastrofeforestillinger
                  </li>
                </ul>
              </div>
            </div>

            {/* Green Arrow */}
            <div className="hidden lg:flex items-center justify-center text-[#123F13] shrink-0">
              <i className="fa-solid fa-arrow-right text-2xl" />
            </div>

            {/* Panel 2: UNDER */}
            <div className="flex-1 bg-white border border-[#E8DDCE] rounded-2xl p-8 relative min-h-[220px] flex flex-col justify-between shadow-sm">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#123F13] block mb-2">UNDER</span>
                <h3 className="text-2xl font-serif text-[#123F13] mb-6 font-semibold">Hvordan virker jeg?</h3>
                <ul className="space-y-3 text-left max-w-xs mx-auto text-[#3B352F]/80">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#123F13] shrink-0" />
                    Selvfokus
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#123F13] shrink-0" />
                    Overvågning
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#123F13] shrink-0" />
                    Kontrol
                  </li>
                </ul>
              </div>
            </div>

            {/* Green Arrow */}
            <div className="hidden lg:flex items-center justify-center text-[#123F13] shrink-0">
              <i className="fa-solid fa-arrow-right text-2xl" />
            </div>

            {/* Panel 3: EFTER */}
            <div className="flex-1 bg-white border border-[#E8DDCE] rounded-2xl p-8 relative min-h-[220px] flex flex-col justify-between shadow-sm">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#8C2F12] block mb-2">EFTER</span>
                <h3 className="text-2xl font-serif text-[#123F13] mb-6 font-semibold">Hvad gjorde jeg forkert?</h3>
                <ul className="space-y-3 text-left max-w-xs mx-auto text-[#3B352F]/80">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Mental gennemgang
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Fejlfinding
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] shrink-0" />
                    Grubleri
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 max-w-3xl mx-auto p-6 border-l-4 border-[#8C2F12] bg-white text-left text-[#3B352F]/80 rounded-r-xl shadow-sm">
            Den sociale situation kan vare en time. Det mentale arbejde omkring den kan begynde flere dage før og fortsætte længe efter.
          </div>
        </div>
      </section>

      {/* "Det er ikke din skyld" Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-white to-[#FAF7F2] border border-[#E8DDCE] p-8 md:p-12 rounded-2xl shadow-sm">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Det er ikke din skyld</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Et forsøg på at passe på dig selv kan blive et mønster
            </h2>
            <p className="text-lg text-[#3B352F]/70 leading-relaxed">
              Bekymring, overvågning og kontrol giver ofte mening som forsøg på at undgå fejl, afvisning eller synlig nervøsitet. Problemet er ikke, at du gør noget forkert med vilje. Problemet kan være, at meget mental kontrol gør det sværere at opdage, hvad der faktisk sker omkring dig.
            </p>
          </div>
        </div>
      </section>

      {/* "Fire steder, hvor social angst kan få mere plads" Section */}
      <section className="py-20 bg-[#123F13] text-white relative overflow-hidden">
        {/* Subtle decorative background glow */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/[0.03] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#8C2F12]/[0.1] rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <span className="text-white/60 font-bold text-xs uppercase tracking-widest">Hvad holder mønstret i gang?</span>
          <h2 className="text-3xl md:text-4xl font-serif text-white mt-3 mb-6 font-semibold">
            Fire steder, hvor social angst kan få mere plads
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mb-12">
            Det handler ikke om én forkert tanke. Det handler ofte om, hvor meget tid og opmærksomhed der bruges på at forberede, overvåge, kontrollere og analysere.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 */}
            <div className="bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xs border border-white/10 hover:border-white/20 p-8 rounded-2xl flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 group">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8C2F12] to-[#A83D1D] text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-black/20 text-lg group-hover:scale-105 transition-transform duration-300">
                  1
                </div>
                <h3 className="text-xl font-serif font-semibold text-white mb-3">Bekymring før</h3>
                <div className="pl-3 border-l-2 border-[#8C2F12] text-white/90 text-sm italic leading-relaxed">
                  “Hvordan undgår jeg, at noget går galt?”
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xs border border-white/10 hover:border-white/20 p-8 rounded-2xl flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 group">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8C2F12] to-[#A83D1D] text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-black/20 text-lg group-hover:scale-105 transition-transform duration-300">
                  2
                </div>
                <h3 className="text-xl font-serif font-semibold text-white mb-3">Selvfokus under</h3>
                <div className="pl-3 border-l-2 border-[#8C2F12] text-white/90 text-sm italic leading-relaxed">
                  “Hvordan ser og lyder jeg lige nu?”
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xs border border-white/10 hover:border-white/20 p-8 rounded-2xl flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 group">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8C2F12] to-[#A83D1D] text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-black/20 text-lg group-hover:scale-105 transition-transform duration-300">
                  3
                </div>
                <h3 className="text-xl font-serif font-semibold text-white mb-3">Sikkerhedsstrategier</h3>
                <div className="pl-3 border-l-2 border-[#8C2F12] text-white/90 text-sm italic leading-relaxed">
                  “Hvad kan jeg gøre for at skjule eller kontrollere det?”
                </div>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xs border border-white/10 hover:border-white/20 p-8 rounded-2xl flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 group">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8C2F12] to-[#A83D1D] text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-black/20 text-lg group-hover:scale-105 transition-transform duration-300">
                  4
                </div>
                <h3 className="text-xl font-serif font-semibold text-white mb-3">Grubleri efter</h3>
                <div className="pl-3 border-l-2 border-[#8C2F12] text-white/90 text-sm italic leading-relaxed">
                  “Hvad gjorde jeg forkert?”
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Selvfokus – når opmærksomheden vender indad" Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Når opmærksomheden går indad</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            Selvfokus – når opmærksomheden vender indad
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-3xl mb-12">
            Jo mere du holder øje med, hvordan du virker, desto mindre opmærksomhed er der tilbage til den samtale eller situation, du faktisk står i.
          </p>

          {/* Selvfokus-cirklen */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 relative">
            {/* Step 1 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm">
              <strong className="text-[#123F13] font-bold text-base">Social situation</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du er sammen med andre</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Opmærksomheden går indad</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du begynder at mærke og tjekke</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Jeg overvåger mig selv</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Stemme, ansigt, krop og uro</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Jeg prøver at kontrollere</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du forsøger at styre dit indtryk</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Mere uro</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Og endnu mere behov for at holde øje</span>
            </div>
          </div>
        </div>
      </section>

      {/* "De strategier, der prøver at hjælpe" Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Strategier og kortvarig tryghed</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            De strategier, der prøver at hjælpe
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-3xl mb-12">
            En strategi kan give mening og samtidig komme til at holde problemet i gang.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column: Pills */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Undgå øjenkontakt
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Sige så lidt som muligt
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Planlægge alle svar
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Skjule hænderne
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Kontrollere stemmen
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Holde fast i telefon eller glas
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Stå tæt på en bestemt person
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-full px-5 py-3.5 font-semibold text-[#123F13] shadow-sm text-center">
                Forlade situationen tidligt
              </div>
            </div>

            {/* Right Column: Paradox Stack */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm space-y-4">
              <h3 className="text-xl font-bold text-[#123F13] mb-4 text-center">Sikkerhedsstrategiens paradoks</h3>
              
              <div className="space-y-3">
                <div className="bg-[#FCFAF7] border border-[#E8DDCE]/60 rounded-xl p-4 text-center font-bold text-[#123F13]">
                  Jeg føler mig utryg
                </div>
                <div className="flex justify-center text-[#8C2F12] py-1">
                  <i className="fa-solid fa-arrow-down text-lg" />
                </div>
                <div className="bg-white border border-[#8C2F12]/30 p-4 rounded-xl font-bold text-[#8C2F12] text-center">
                  Jeg bruger en strategi
                </div>
                <div className="flex justify-center text-[#8C2F12] py-1">
                  <i className="fa-solid fa-arrow-down text-lg" />
                </div>
                <div className="bg-[#FAF7F2] border border-[#E8DDCE]/60 p-4 rounded-xl font-bold text-[#123F13] text-center">
                  Kortvarig lettelse
                </div>
                <div className="flex justify-center text-[#8C2F12] py-1">
                  <i className="fa-solid fa-arrow-down text-lg" />
                </div>
                <div className="bg-[#123F13] text-white p-4 rounded-xl font-bold text-center">
                  Mindre mulighed for at opdage, hvad der sker uden strategien
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Når situationen er slut – men hovedet fortsætter" Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Når tankerne fortsætter bagefter</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-2 mb-6">Når situationen er slut – men hovedet fortsætter</h2>
          
          <div className="font-serif text-2xl md:text-3xl text-[#123F13] italic max-w-2xl mx-auto leading-relaxed mb-12">
            Samtalen er slut.<br />Du er kommet hjem.<br />Men hovedet arbejder stadig.
          </div>

          {/* Cycle 2 */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 relative max-w-5xl mx-auto mb-12">
            {/* Step 1 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm">
              <strong className="text-[#123F13] font-bold text-base">Situation slut</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du er ikke længere i den</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Mental gennemgang</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du spiller øjeblikke igennem</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Fejlfinding</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Du leder efter det, der gik galt</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Mere usikkerhed</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Tvivlen vokser</span>
              <div className="hidden lg:flex absolute lg:top-1/2 lg:-right-4 lg:-translate-y-1/2 w-8 h-8 rounded-full bg-[#8C2F12] text-white items-center justify-center shadow-sm z-10">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl flex flex-col justify-center min-h-[140px] relative shadow-sm mt-4 md:mt-0">
              <strong className="text-[#123F13] font-bold text-base">Større frygt næste gang</strong>
              <span className="text-[#3B352F]/70 text-sm mt-2">Den næste situation føles farligere</span>
            </div>
          </div>

          <div className="text-sm font-bold text-[#8C2F12] bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-xl p-4 max-w-2xl mx-auto italic">
            “Hvorfor sagde jeg det?” &nbsp;·&nbsp; “Kunne de se, at jeg var nervøs?” &nbsp;·&nbsp; “Var jeg kedelig?”
          </div>
        </div>
      </section>

      {/* "En anden måde at arbejde med social angst på" Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Metakognitiv terapi</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            En anden måde at arbejde med social angst på
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-3xl mb-12">
            Jeg arbejder ikke kun med, hvad dine tanker siger. Jeg ser også på, hvor længe du bliver optaget af dem, hvordan du bruger din opmærksomhed, hvad du prøver at kontrollere, og hvad der sker, når den sociale situation er slut.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white border border-[#E8DDCE] p-8 rounded-2xl shadow-sm">
              <div className="w-10 h-10 rounded-full bg-[#8C2F12] text-white font-bold flex items-center justify-center mb-6">
                1
              </div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Opdage mønsteret</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Hvad sker der før, under og efter – og hvor bliver din opmærksomhed fanget?
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white border border-[#E8DDCE] p-8 rounded-2xl shadow-sm">
              <div className="w-10 h-10 rounded-full bg-[#8C2F12] text-white font-bold flex items-center justify-center mb-6">
                2
              </div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Træne større fleksibilitet</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Kan opmærksomheden være mindre låst fast på dig selv og mere fri til at bevæge sig?
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white border border-[#E8DDCE] p-8 rounded-2xl shadow-sm">
              <div className="w-10 h-10 rounded-full bg-[#8C2F12] text-white font-bold flex items-center justify-center mb-6">
                3
              </div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Mindske det mentale efterarbejde</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Behøver samtalen fortsætte inde i hovedet resten af aftenen?
              </p>
            </div>
          </div>

          <div className="mt-12 text-left">
            <Link to="/metakognitiv-terapi" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
              Læs mere om metakognitiv terapi <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* "Fra indre kontrolrum til mere kontakt med situationen" Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Fleksibel opmærksomhed</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            Fra indre kontrolrum til mere kontakt med situationen
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-3xl mb-12">
            Målet er ikke at kontrollere opmærksomheden perfekt. Målet er større fleksibilitet.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Column 1: Indad */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-[#8C2F12] mb-6 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#8C2F12]" />
                Opmærksomheden fanget indad
              </h3>
              <ul className="space-y-4 text-[#3B352F]/80">
                <li className="flex items-start gap-3">
                  <span className="text-[#8C2F12] font-bold mt-0.5">&bull;</span>
                  <span>Hvordan lyder min stemme?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8C2F12] font-bold mt-0.5">&bull;</span>
                  <span>Kan de se, jeg er nervøs?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8C2F12] font-bold mt-0.5">&bull;</span>
                  <span>Rødmer jeg?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8C2F12] font-bold mt-0.5">&bull;</span>
                  <span>Hvad skal jeg sige bagefter?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8C2F12] font-bold mt-0.5">&bull;</span>
                  <span>Hvordan virker jeg?</span>
                </li>
              </ul>
            </div>

            {/* Column 2: Udad */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-[#123F13] mb-6 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#123F13]" />
                Mere kontakt med situationen
              </h3>
              <ul className="space-y-4 text-[#3B352F]/80">
                <li className="flex items-start gap-3">
                  <span className="text-[#123F13] font-bold mt-0.5">&bull;</span>
                  <span>Hvad siger personen faktisk?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#123F13] font-bold mt-0.5">&bull;</span>
                  <span>Hvad sker der omkring mig?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#123F13] font-bold mt-0.5">&bull;</span>
                  <span>Hvem taler jeg med?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#123F13] font-bold mt-0.5">&bull;</span>
                  <span>Hvad hører jeg lige nu?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#123F13] font-bold mt-0.5">&bull;</span>
                  <span>Hvad vil jeg gerne bidrage med?</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-[#123F13] text-white p-6 rounded-2xl text-center font-bold text-base">
            Det handler ikke om, at du aldrig må mærke efter indad. Det handler om, at opmærksomheden ikke behøver være låst fast.
          </div>
        </div>
      </section>

      {/* "Hypnose – kun hvis det giver mening" Section */}
      <section className="py-16 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border border-[#E8DDCE] p-8 md:p-12 rounded-2xl shadow-sm">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Hypnose – kun hvis det giver mening</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Rolig støtte, ikke kontroltab
            </h2>
            <p className="text-lg text-[#3B352F]/70 leading-relaxed mb-6">
              Hypnose kan indgå som en valgfri støtte, hvis det giver mening for dig. Jeg bruger hypnose som rolig guidning og fokuseret opmærksomhed – ikke som kontroltab eller som en metode, der skal fjerne alle tanker eller al nervøsitet.
            </p>
            <Link to="/hypnose" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
              Læs hvordan jeg bruger hypnose <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* "Start med ét konkret første skridt" Section */}
      <section id="foerste-session" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Første behandlingssession</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Start med ét konkret første skridt
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Du behøver ikke beslutte dig for et helt forløb. Den første session handler om at skabe overblik over dit mønster og finde et tydeligt næste skridt.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch max-w-5xl mx-auto">
            {/* Left Price Card */}
            <div className="lg:col-span-5 bg-[#123F13] text-white rounded-2xl p-8 flex flex-col justify-between shadow-md">
              <div>
                <h3 className="text-2xl font-serif mb-4 text-white font-semibold">Første behandlingssession</h3>
                <div className="font-serif text-6xl md:text-7xl font-light my-6 text-white leading-none">
                  375 kr.
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  50 minutter &bull; kun for nye klienter &bull; Valby eller online.
                </p>
                <p className="text-white/60 text-xs italic">
                  Betaling sker altid inden sessionen starter.
                </p>
              </div>
              <div className="pt-8">
                <Link 
                  to="/bestil-tid" 
                  className="w-full inline-flex items-center justify-center bg-[#8C2F12] text-white px-6 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-sm"
                >
                  Book første behandlingssession
                </Link>
              </div>
            </div>

            {/* Right Session List */}
            <div className="lg:col-span-7 bg-white border border-[#E8DDCE] rounded-2xl p-8 flex flex-col justify-between shadow-sm">
              <div>
                <h3 className="text-2xl font-serif text-[#123F13] mb-6 font-semibold">Sessionen kan indeholde</h3>
                <ul className="space-y-4 text-[#3B352F]/80">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Personlig kortlægning af dit mønster</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Forståelse af, hvad der sker før, under og efter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Konkrete øvelser</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Praktiske teknikker</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Et tydeligt næste skridt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>En personlig behandlingsplan</span>
                  </li>
                </ul>
              </div>
              <div className="pt-8 border-t border-[#E8DDCE]/60 mt-6">
                <p className="font-bold text-[#123F13] text-sm">
                  Du behøver ikke binde dig til et forløb.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Praktisk at vide efter den første session" Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Priser og vilkår</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Praktisk at vide efter den første session
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Den første behandlingssession til 375 kr. er kun for nye klienter. Derefter kan du vælge den løsning, der passer bedst til dig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Card 1 */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-[#123F13] text-lg mb-3">Enkelt session</h4>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Normalpris: <strong className="text-[#123F13]">1.800 kr.</strong><br />
                Studerende og pensionister: <strong className="text-[#123F13]">1.350 kr.</strong>
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-[#123F13] text-lg mb-3">Core-rabat ved lægehenvisning</h4>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                300 kr. rabat på normalprisen.<br />
                Pris: <strong className="text-[#123F13]">1.500 kr.</strong> pr. session.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-[#123F13] text-lg mb-3">Klippekort</h4>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                5 sessioner: <strong className="text-[#123F13]">8.500 kr.</strong> (gyldig i 4 mdr.)<br />
                10 sessioner: <strong className="text-[#123F13]">15.000 kr.</strong> (gyldig i 8 mdr.)<br />
              </p>
            </div>
          </div>

          {/* Cancellation Note */}
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-center max-w-4xl mx-auto shadow-sm">
            <div className="w-16 h-16 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] font-serif text-3xl font-bold flex items-center justify-center shrink-0">
              24
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#123F13] mb-2">Afbud eller ændring senest 24 timer før</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Ved senere afbud koster det et klip fra klippekortet eller fuld pris for sessionen. Reglen gælder også ved akut sygdom. Hvis du ikke kan møde fysisk i klinikken, kan den planlagte session gennemføres online i stedet.
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-4xl mx-auto border-t border-[#E8DDCE]/60 pt-6">
            <Link to="/priser" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
              Se alle priser og muligheder <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <span className="text-xs text-[#3B352F]/60 font-medium">Betaling sker altid inden sessionen starter.</span>
          </div>
        </div>
      </section>

      {/* "To måder at mødes på" Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Valby eller online</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
            To måder at mødes på
          </h2>
          <p className="text-lg text-[#3B352F]/70 max-w-3xl mb-12">
            Hvilken form der passer bedst, afhænger af dig og din situation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl">
            {/* Location 1 */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center mb-6">
                  <MapPin size={20} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">I klinikken i Valby</h3>
                <p className="text-[#3B352F]/80 text-sm leading-relaxed mb-4">
                  Valby Tingsted 7<br />2500 Valby
                </p>
              </div>
              <p className="text-xs text-[#3B352F]/50 italic">Et fysisk møde i klinikken.</p>
            </div>

            {/* Location 2 */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center mb-6">
                  <Laptop size={20} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">Online</h3>
                <p className="text-[#3B352F]/80 text-sm leading-relaxed mb-4">
                  Fra et sted, hvor du kan sidde uforstyrret.
                </p>
              </div>
              <p className="text-xs text-[#3B352F]/50 italic">
                Online kan være en praktisk mulighed, hvis transport, afstand eller nye omgivelser tager meget energi.
              </p>
            </div>
          </div>

          <div className="mt-12">
            <Link to="/online-sessioner" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
              Læs mere om online-sessioner <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#FAF7F2] border-t border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Ofte stillede spørgsmål</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-4 font-semibold">
              Spørgsmål, mange har før de tager kontakt
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Åbn et spørgsmål og få svaret forklaret visuelt, roligt og konkret.
            </p>
          </div>

          {/* FAQ Accordion List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start max-w-6xl">
            <div className="space-y-4">
              {faqData.slice(0, Math.ceil(faqData.length / 2)).map((faq, index) => {
                const actualIndex = index;
                return (
                  <FAQItem
                    key={actualIndex}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFaqIndex === actualIndex}
                    onToggle={() => toggleFaq(actualIndex)}
                  />
                );
              })}
            </div>
            <div className="space-y-4">
              {faqData.slice(Math.ceil(faqData.length / 2)).map((faq, index) => {
                const actualIndex = index + Math.ceil(faqData.length / 2);
                return (
                  <FAQItem
                    key={actualIndex}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFaqIndex === actualIndex}
                    onToggle={() => toggleFaq(actualIndex)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Fagligt grundlag, Kilder, Relevante sider & Et første skridt */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-[#FCFAF7] border-t border-[#E8DDCE] py-20">
        <div className="max-w-6xl mx-auto px-6 space-y-20">
          
          {/* Fagligt grundlag */}
          <div>
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-3">Fagligt grundlag</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] font-semibold mb-6">
              Social angst handler ofte om mere end selve situationen
            </h2>
            <p className="text-[#3B352F]/80 max-w-4xl text-base md:text-lg leading-relaxed mb-10">
              Kliniske modeller og forskning i social angst har i mange år peget på betydningen af det, der kan ske før, under og efter sociale situationer: bekymring og mental forberedelse, stærkt selvfokus og efterfølgende mental gennemgang.
            </p>

            {/* Før, Under, Efter columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-xs">
                <h3 className="text-lg font-bold text-[#123F13] mb-2">Før situationen</h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                  Forventninger, bekymring og mentale forestillinger kan gøre situationen belastende, længe før den begynder.
                </p>
              </div>

              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-xs">
                <h3 className="text-lg font-bold text-[#123F13] mb-2">Under situationen</h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                  Når opmærksomheden bliver låst fast på stemmen, kroppen og spørgsmålet “Hvordan virker jeg?”, kan det blive sværere at være i kontakt med det, der faktisk sker.
                </p>
              </div>

              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-xs">
                <h3 className="text-lg font-bold text-[#123F13] mb-2">Efter situationen</h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                  Mental gennemgang og fejlfinding kan forlænge uroen og gøre næste sociale situation mere truende.
                </p>
              </div>
            </div>

            {/* Se faglige kilder */}
            <details className="group bg-white border border-[#E8DDCE] rounded-2xl overflow-hidden shadow-sm">
              <summary className="px-6 py-5 font-bold text-[#123F13] bg-[#FAF7F2]/30 cursor-pointer flex justify-between items-center select-none list-none [&::-webkit-details-marker]:hidden">
                <span className="md:text-lg">Se faglige kilder</span>
                <span className="text-[#123F13] transition-transform duration-300 group-open:rotate-180">
                  <ChevronDown size={20} />
                </span>
              </summary>
              <div className="px-6 py-6 text-[#3B352F]/70 text-sm space-y-4 border-t border-[#E8DDCE]/40">
                <p>Denne side læner sig blandt andet op ad kliniske retningslinjer og klassiske modeller for social angst samt metakognitiv teori:</p>
                <ul className="space-y-3">
                  <li className="border border-[#E8DDCE]/40 rounded-xl p-4 bg-[#FAF7F2] hover:bg-[#FAF7F2]/80 transition-colors">
                    <a href="https://www.nice.org.uk/guidance/cg159" target="_blank" rel="noopener noreferrer" className="flex justify-between items-center group/link">
                      <div>
                        <strong className="text-[#123F13] block">NICE (CG159)</strong>
                        <span className="text-xs italic text-[#3B352F]/70">Social anxiety disorder: recognition, assessment and treatment</span>
                      </div>
                      <span className="text-xs font-bold text-[#8C2F12] shrink-0 group-hover/link:underline">Åbn kilde ↗</span>
                    </a>
                  </li>
                  <li className="border border-[#E8DDCE]/40 rounded-xl p-4 bg-[#FAF7F2] hover:bg-[#FAF7F2]/80 transition-colors">
                    <a href="https://scholar.google.com/scholar?q=%22A+cognitive+model+of+social+phobia%22+Clark+Wells+1995" target="_blank" rel="noopener noreferrer" className="flex justify-between items-center group/link">
                      <div>
                        <strong className="text-[#123F13] block">Clark, D. M. &amp; Wells, A. (1995)</strong>
                        <span className="text-xs italic text-[#3B352F]/70">A cognitive model of social phobia</span>
                      </div>
                      <span className="text-xs font-bold text-[#8C2F12] shrink-0 group-hover/link:underline">Find publikationen ↗</span>
                    </a>
                  </li>
                  <li className="border border-[#E8DDCE]/40 rounded-xl p-4 bg-[#FAF7F2] hover:bg-[#FAF7F2]/80 transition-colors">
                    <a href="https://books.google.com/books?q=%22Metacognitive+Therapy+for+Anxiety+and+Depression%22+Adrian+Wells" target="_blank" rel="noopener noreferrer" className="flex justify-between items-center group/link">
                      <div>
                        <strong className="text-[#123F13] block">Wells, A. (2009)</strong>
                        <span className="text-xs italic text-[#3B352F]/70">Metacognitive Therapy for Anxiety and Depression</span>
                      </div>
                      <span className="text-xs font-bold text-[#8C2F12] shrink-0 group-hover/link:underline">Find bogen ↗</span>
                    </a>
                  </li>
                </ul>
                <p className="text-xs text-[#3B352F]/50 italic pt-2">Kilderne bruges som fagligt grundlag. Behandlingen tilpasses altid den enkelte person og den konkrete situation.</p>
              </div>
            </details>
          </div>

          {/* Relevante sider */}
          <div className="border-t border-[#E8DDCE]/60 pt-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-3">Relevante sider</span>
            <h2 className="text-2xl md:text-3xl font-serif text-[#123F13] font-semibold mb-8">
              Måske er disse sider også relevante
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/angst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 text-center font-bold text-[#123F13] hover:bg-[#123F13] hover:text-white hover:border-[#123F13] transition-all shadow-xs">
                Angst &rarr;
              </Link>
              <Link to="/panikangst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 text-center font-bold text-[#123F13] hover:bg-[#123F13] hover:text-white hover:border-[#123F13] transition-all shadow-xs">
                Panikangst &rarr;
              </Link>
              <Link to="/tankemylder" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 text-center font-bold text-[#123F13] hover:bg-[#123F13] hover:text-white hover:border-[#123F13] transition-all shadow-xs">
                Tankemylder &rarr;
              </Link>
              <Link to="/metakognitiv-terapi" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 text-center font-bold text-[#123F13] hover:bg-[#123F13] hover:text-white hover:border-[#123F13] transition-all shadow-xs">
                Metakognitiv terapi &rarr;
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Et første skridt */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-[#123F13] text-white py-16 md:py-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
            <div className="mb-6">
              <img 
                src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
                alt="Core Kvalitetsstempel" 
                className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain"
              />
            </div>
            <span className="text-white/60 font-bold text-xs uppercase tracking-widest block mb-3">Et første skridt</span>
            <h2 className="text-3xl md:text-4xl font-serif text-white font-semibold mb-4">
              Du behøver ikke først <br /> blive helt fri for nervøsitet
            </h2>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Målet er ikke nødvendigvis, at du aldrig mærker uro eller får en usikker tanke. Det vigtigste kan være, at de ikke længere skal styre så meget af din opmærksomhed, din tid og dine valg.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
              <Link 
                to="/bestil-tid" 
                className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md"
              >
                Book første behandlingssession
              </Link>
              <a 
                href="tel:+4527204671" 
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-white/10 transition-all"
              >
                Ring 27 20 46 71
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SocialAngst;
